/**
 * Local-first fallbacks for every image the SDK would otherwise serve
 * straight from Mappedin's own cloud (`LocationProfile.icon`/`images`,
 * `LocationCategory.icon`). Those all sit behind a short-lived, SAS-signed
 * CDN URL — and even constructing that URL requires a live call to
 * Mappedin's API (`/sas-token`), so they never load once the kiosk is
 * actually offline, unlike the map's own geometry/nav graph (which hydrates
 * from a local MVF bundle — see `hydrateFromLocalBundle` in mappedin.ts).
 *
 * Resolution is a lookup against `public/local-assets-manifest.json` — one
 * small file, fetched once and cached here, listing exactly which local
 * file exists for which key (an externalId, or a slugified category name).
 * That manifest is (re)built by scripts/build-asset-manifest.mjs — after
 * `npm run fetch-mvf`, and automatically on every `vite dev`/`vite build`
 * (see vite.config.ts). Looking anything up here is a plain in-memory
 * object read, never a network probe: with ~200 stands, probing every
 * candidate file extension per stand over the network (the previous
 * approach) meant hundreds of speculative, mostly-404 requests at boot —
 * enough to make the kiosk look hung before the map even appeared.
 */

import { config } from "./config";

export type AssetKind = "stands" | "logos" | "photos" | "categoryIcons";

const ASSET_DIR: Record<AssetKind, string> = {
  stands: "stands",
  logos: "logos",
  photos: "photos",
  categoryIcons: "category-icons",
};

type AssetManifest = Record<AssetKind, Record<string, string>>;

const EMPTY_MANIFEST: AssetManifest = { stands: {}, logos: {}, photos: {}, categoryIcons: {} };

let manifestPromise: Promise<AssetManifest> | null = null;

/**
 * Fetches and caches public/local-assets-manifest.json — a missing/unreachable
 * manifest just means "no local files yet", not an error. A failed or non-OK
 * fetch (e.g. a dev-server timing hiccup on first load) is NOT cached as the
 * final answer: caching that would permanently lock the whole session into
 * "no local assets ever" for every stand/logo/photo, even once the manifest
 * becomes reachable again — only a successfully parsed manifest is kept.
 */
function loadManifest(): Promise<AssetManifest> {
  if (!manifestPromise) {
    manifestPromise = fetch("/local-assets-manifest.json")
      .then((res) => (res.ok ? (res.json() as Promise<AssetManifest>) : Promise.reject(new Error(`${res.status}`))))
      .catch(() => {
        manifestPromise = null;
        return EMPTY_MANIFEST;
      });
  }
  return manifestPromise;
}

/**
 * Looks up `key` (an externalId, a slugified category name, or a Mappedin
 * internal location id — see below) in the given asset kind's manifest,
 * returning its local URL or `fallback`.
 *
 * `key` can be a single string or an array of candidates tried in order,
 * returning the first that has a local file — needed because
 * `scripts/fetch-full-mvf.mjs` keys a saved logo/photo by a location's own
 * External ID when it has one, falling back to its internal `id` (e.g.
 * `loc_8220b1e8d5fb9849`, always present) otherwise, while a caller here
 * may have more than one candidate for "this stand's External ID" (its
 * LocationProfile's own, or its Space's). Picking just one candidate with
 * `||` and giving up if that exact string isn't in the manifest can miss a
 * file that a DIFFERENT candidate would have matched.
 *
 * `fallback` is dropped (returns `undefined` instead) when `mapMode` is
 * `"offline"` and no local file was found: that fallback is always one of
 * Mappedin's own signed CDN URLs (a location/category's `icon`/`images`
 * getter), which can't load without hitting Mappedin's cloud first — see
 * this file's own top comment for why. In offline mode, keeping it would
 * silently create a logo/photo marker whose image never loads (a broken
 * image icon on the map) instead of the graceful "no logo" fallback the
 * rest of the UI already has (e.g. `addExhibitorLabels`' text-label
 * fallback). Only stands with no local file at all are affected — one
 * with a matching `public/logos`/`public/stands`/`public/category-icons`
 * file always resolves to that, online or off.
 */
export async function resolveLocalAsset(
  kind: AssetKind,
  key: string | (string | undefined)[],
  fallback: string | undefined,
): Promise<string | undefined> {
  const manifest = await loadManifest();
  for (const candidate of Array.isArray(key) ? key : [key]) {
    if (!candidate) continue;
    const fileName = manifest[kind]?.[candidate];
    if (fileName) return `/${ASSET_DIR[kind]}/${fileName}`;
  }
  return config.mapMode === "offline" ? undefined : fallback;
}

/** Filename-safe slug for keying local assets off a display name rather than an externalId — must match scripts/build-asset-manifest.mjs's own key derivation (the file's basename). */
export function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
